import { useTranslation } from 'react-i18next';
import { Solana, Receipt1, Chart21 } from 'iconsax-react';
import InfoText from '../../atoms/InfoText';

interface BiddingPostTabsProps {
  activeTab: 'information' | 'proposals' | 'hired';
  onTabChange: (tab: 'information' | 'proposals' | 'hired') => void;
  proposalsCount: number;
  hiredCount: number;
}

export default function BiddingPostTabs({
  activeTab,
  onTabChange,
  proposalsCount,
  hiredCount,
}: BiddingPostTabsProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex items-center gap-6 sm:gap-10 md:gap-14 border-b border-light-input-border dark:border-dark-input-border bg-white dark:bg-dark-surface-primary px-4 sm:px-6 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-nowrap w-full"
      data-testid="bidding-post-tabs-container"
    >
      {/* Information Tab */}
      <button
        type="button"
        onClick={() => onTabChange('information')}
        className={`flex items-center gap-2 sm:gap-2.5 py-3.5 px-1 -mb-px border-b-2 font-bold text-sm transition-all shrink-0 whitespace-nowrap ${activeTab === 'information'
          ? 'border-light-text-primary dark:border-dark-text-primary text-light-text-primary dark:text-dark-text-primary'
          : 'border-transparent text-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
          }`}
        data-testid="bidding-post-tab-information"
      >
        <Receipt1
          size={20}
          variant="Bold"
          color="currentColor"
          className={
            activeTab === 'information'
              ? 'text-light-text-primary dark:text-dark-text-primary'
              : 'text-text-secondary dark:text-dark-text-secondary'
          }
        />
        <InfoText
          text={t('biddingPosts.tabInformation')}
          className={`!text-sm !font-bold ${activeTab === 'information'
            ? 'text-light-text-primary dark:text-dark-text-primary'
            : 'text-text-secondary dark:text-dark-text-secondary'
            }`}
        />
      </button>

      {/* Proposals Tab */}
      <button
        type="button"
        onClick={() => onTabChange('proposals')}
        className={`flex items-center gap-2 sm:gap-2.5 py-3.5 px-1 -mb-px border-b-2 font-medium text-sm transition-all shrink-0 whitespace-nowrap ${activeTab === 'proposals'
          ? 'border-light-text-primary dark:border-dark-text-primary text-light-text-primary dark:text-dark-text-primary font-bold'
          : 'border-transparent text-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
          }`}
        data-testid="bidding-post-tab-proposals"
      >
        <Solana
          size={20}
          variant="Bold"
          color="currentColor"
          className={
            activeTab === 'proposals'
              ? 'text-light-text-primary dark:text-dark-text-primary'
              : 'text-text-secondary dark:text-dark-text-secondary'
          }
        />
        <InfoText
          text={t('biddingPosts.tabProposals')}
          className={`!text-sm ${activeTab === 'proposals'
            ? '!font-bold text-light-text-primary dark:text-dark-text-primary'
            : '!font-normal text-text-secondary dark:text-dark-text-secondary'
            }`}
        />
        <div
          className="px-2 py-0.5 min-h-[26px] rounded-[8px] bg-light-information dark:bg-dark-information text-light-button-label-primary flex items-center justify-center gap-2"
          data-testid="bidding-post-tab-proposals-badge"
        >
          <InfoText text={`${proposalsCount}`} className="!text-sm !font-semibold text-light-button-label-primary leading-none" />
        </div>
      </button>

      {/* Hired Tab */}
      <button
        type="button"
        onClick={() => onTabChange('hired')}
        className={`flex items-center gap-2 sm:gap-2.5 py-3.5 px-1 -mb-px border-b-2 font-medium text-sm transition-all shrink-0 whitespace-nowrap ${activeTab === 'hired'
          ? 'border-light-text-primary dark:border-dark-text-primary text-light-text-primary dark:text-dark-text-primary font-bold'
          : 'border-transparent text-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
          }`}
        data-testid="bidding-post-tab-hired"
      >
        <Chart21
          size={20}
          variant="Bold"
          color="currentColor"
          className={
            activeTab === 'hired'
              ? 'text-light-text-primary dark:text-dark-text-primary'
              : 'text-text-secondary dark:text-dark-text-secondary'
          }
        />
        <InfoText
          text={t('biddingPosts.tabHired')}
          className={`!text-sm ${activeTab === 'hired'
            ? '!font-bold text-light-text-primary dark:text-dark-text-primary'
            : '!font-normal text-text-secondary dark:text-dark-text-secondary'
            }`}
        />
        {hiredCount > 0 && (
          <div
            className="px-2 py-0.5 min-h-[26px] rounded-[8px] bg-light-success dark:bg-dark-success text-light-button-label-primary flex items-center justify-center gap-2"
            data-testid="bidding-post-tab-hired-badge"
          >
            <InfoText text={`${hiredCount}`} className="!text-sm !font-semibold text-light-button-label-primary leading-none" />
          </div>
        )}
      </button>
    </div>
  );
}

